package com.karawek.core.ui.components

import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import com.karawek.core.ui.theme.CoreAlphas
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.util.DateFormatter

/**
 * A date text field that automatically formats the input as mm/dd/yyyy.
 *
 * @param value The date in mm/dd/yyyy format (e.g., "01/15/2024")
 * @param onValueChange Callback with date in mm/dd/yyyy format (e.g., "01/15/2024")
 * @param label The label for the text field
 * @param placeholder The placeholder text
 * @param modifier The modifier to be applied to the text field
 * @param enabled Whether the text field is enabled
 * @param error Error message to display, if any
 */
@Composable
fun CoreDateTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    placeholder: String = "",
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    error: String? = null,
) {
    var textFieldValue by remember(value) {
        mutableStateOf(
            TextFieldValue(
                text = value,
                selection = TextRange(value.length)
            )
        )
    }

    OutlinedTextField(
        value = textFieldValue,
        onValueChange = { newTextFieldValue ->
            val digitsOnly = newTextFieldValue.text.filter { it.isDigit() }
            val formatted = DateFormatter.formatAsTyping(digitsOnly)

            // Calculate new cursor position
            val oldDigitCount = textFieldValue.text.take(textFieldValue.selection.start).count { it.isDigit() }
            val newDigitCount = newTextFieldValue.text.take(newTextFieldValue.selection.start).count { it.isDigit() }
            val digitDiff = newDigitCount - oldDigitCount

            var newCursorPos = 0
            var digitsSeen = 0
            for (i in formatted.indices) {
                if (formatted[i].isDigit()) {
                    digitsSeen++
                    if (digitsSeen > oldDigitCount + digitDiff) break
                }
                newCursorPos = i + 1
            }

            textFieldValue = TextFieldValue(
                text = formatted,
                selection = TextRange(newCursorPos.coerceIn(0, formatted.length))
            )

            onValueChange(formatted)
        },
        label = { Text(label) },
        placeholder = { Text(placeholder) },
        modifier = modifier,
        enabled = enabled,
        isError = error != null,
        textStyle = CoreTypography.body,
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
        supportingText = {
            error?.let {
                Text(
                    text = it,
                    style = CoreTypography.caption,
                    color = CoreTheme.colors.error,
                )
            }
        },
        colors = androidx.compose.material3.OutlinedTextFieldDefaults.colors(
            focusedBorderColor = CoreTheme.colors.primary,
            focusedLabelColor = CoreTheme.colors.primary,
            errorBorderColor = CoreTheme.colors.error,
            errorLabelColor = CoreTheme.colors.error,
            disabledBorderColor = CoreTheme.colors.divider.copy(alpha = CoreAlphas.Disabled),
            disabledLabelColor = CoreTheme.colors.textSecondary.copy(alpha = CoreAlphas.Disabled),
        ),
    )
}
